import type { ProjectDocument } from '../models/project/types';
import type { VersionDocument } from '../models/version';
import type { BranchRepresentationType } from '@readme/api-contracts/schemas/branch';
import type { Request } from 'express';

import * as URL from 'url';

import { hasVersionBranch, isVersionName, parseVersionBranch } from '../iso';
import isLang from '../utils/isLang';

const VERSION_REGEX = /(?!v)([0-9]+)(?:\.([0-9]+))?(?:\.([0-9]+))?(-.*)?/;

const legacyLangPrefix = 'lang-';

export interface Options {
  branch?: BranchRepresentationType;
  child?: boolean;
  hasOneChild?: boolean;
  lang?: string;
  langPrefix?: string;
  parent?: ProjectDocument | null;
  project?: ProjectDocument | null;
  version?: VersionDocument | null;
}

const defaultOptions = {
  child: false,
  parent: null,
  hasOneChild: false,
  project: null,
  version: null,
  lang: 'en',
};

export interface Parsed {
  api?: boolean;
  branch?: string;
  child?: string;
  hash?: string;
  host?: string;
  hostname?: string;
  is_version_stable?: boolean;
  lang?: string;
  langPrefix?: string;
  protocol?: string;
  query?: string;
  slugs?: string[];
  version?: string;
}

class UrlManager {
  options: Options;

  defaults: Parsed;

  constructor(providedOpts?: Options) {
    const options = { ...defaultOptions, ...providedOpts };
    this.options = options;

    if (this.options.project?.flags?.translation) {
      this.options.langPrefix = legacyLangPrefix;
    }

    this.defaults = {
      branch: options.branch?.name,
      child:
        options.child || (options.parent && !options.hasOneChild)
          ? UrlManager.getChildName(options.project)
          : undefined,
      lang: options.lang,
      version: options.version?.version,
    };
  }

  static optionsFrom(req: Request) {
    const maybeParent = req.project?._parent;
    const parent = (maybeParent &&
      ('toObject' in maybeParent ? maybeParent.toObject() : { ...maybeParent })) as ProjectDocument;

    if (parent) {
      // @ts-expect-error we're trying to reassign `parent.childrenProjects` to an array of strings
      // which does not match the type at all.
      parent.childrenProjects = req.project._parent.childrenProjects.map(UrlManager.getChildName);
    }

    const hasOneChild = parent && parent.childrenProjects.length === 1 && parent.flags.singleProjectEnterprise;

    return {
      ...defaultOptions,
      parent,
      hasOneChild,
      project: req.project,
      lang: req.params?.lang || 'en',
      langPrefix: req.project?.flags?.translation ? legacyLangPrefix : undefined,
      version: req.version,
      branch: req.branch,
    };
  }

  static getChildName(child: ProjectDocument | null): string {
    return child?.subpath ? child.subpath : (child?.subdomain ?? '');
  }

  static fromReq(req: Request): UrlManager {
    return new UrlManager(UrlManager.optionsFrom(req));
  }

  getChild(firstPathPiece: string): string | null {
    if (this.options.child) return this.defaults.child || null;
    if (!this.options.parent) return null;

    // `parent.childrenProjects` is an array of string subdomains at this point,
    // which does not match the type at all
    return (
      (this.options.parent.childrenProjects as unknown as string[])?.find(subdomain => subdomain === firstPathPiece) ||
      null
    );
  }

  parse(originalPath: string, useDefaults = false, parseLang = true) {
    let path = originalPath;
    if (typeof window !== 'undefined' && !path) path = window.location.pathname;
    // todo: we should update this url.parse has some crunchy security issues.
    // new URL(path);
    const parsedUrl = URL.parse(path);
    const split = parsedUrl.pathname?.split('/').filter(p => p) || [];

    const child = this.getChild(split[0]);

    let parsed = parsedUrl as Parsed;
    if (child) {
      parsed.child = child;
      if (parsed.child && parsed.child === split[0]) split.shift();
      if (this.options.hasOneChild) parsed.child = undefined;
    }

    if (split[0] === 'api') {
      parsed.api = true;
      split.shift();
    }

    if (parseLang && isLang.test(split[0])) {
      parsed.lang = split[0]?.includes(legacyLangPrefix) ? split[0].replace(legacyLangPrefix, '') : split[0];
      split.shift();
    }

    let versionBranch: ReturnType<typeof parseVersionBranch> = {
      version: split[0],
      branch: '',
    };

    if (split[0] && hasVersionBranch(split[0])) {
      versionBranch = parseVersionBranch(split[0]);
    }

    if (versionBranch.version && isVersionName(versionBranch.version)) {
      const match = versionBranch.version.match(VERSION_REGEX) as RegExpMatchArray;
      parsed.version = match[0];
      parsed.branch = undefined;
      split.shift();
    }

    if (versionBranch?.branch) {
      // We need the non-prefixed name of the branch, e.g. 1.0_branch-name.
      parsed.branch = decodeURIComponent(versionBranch.branch.replace(/^v/i, ''));
    }

    parsed.slugs = split;

    if (useDefaults) {
      parsed = { ...this.defaults, ...parsed };
    }

    if (parsed.version && !versionBranch.branch) {
      parsed.is_version_stable = parsed.version === this.defaults.version && this.options.version?.is_stable;
    }

    return parsed;
  }

  stringify(parsed: Parsed, absolute = false): string {
    let total = '';
    if (parsed.child) {
      total += `/${parsed.child}`;
    }

    if (parsed.api) {
      total += '/api';
    }

    if (parsed.lang && parsed.lang !== 'en') {
      const langPrefix = this.options.langPrefix || '';
      total += `/${langPrefix}${parsed.lang}`;
    }

    // We only include either the branch or version, but never both.
    if (parsed.branch) {
      total += `/v${encodeURIComponent(parsed.branch)}`;
    } else if (parsed.version && !parsed.is_version_stable) {
      total += `/v${parsed.version}`;
    }

    if (parsed.slugs?.length) {
      const slugs = parsed.slugs.join('/');
      total += `/${slugs}`;
    }

    if (parsed.query) {
      total += `?${parsed.query}`;
    }

    if (parsed.hash) {
      total += `${parsed.hash}`;
    }

    if (total === '') {
      total = '/';
    }

    if (absolute) {
      return `${parsed.protocol}//${parsed.hostname}${total}`;
    }

    return total;
  }

  upsert(type: string, value: unknown, path: string): string {
    const parsed = this.parse(path);
    parsed[type] = value;
    return this.stringify(parsed);
  }

  remove(type: string, path: string, absolute = false): string {
    const parsed = this.parse(path);
    delete parsed[type];
    return this.stringify(parsed, absolute);
  }

  generate(path: string, options: Options): string {
    const parsed = this.parse(path, true);

    for (const property in options) {
      if (!options[property]) delete parsed[property];
    }
    return this.stringify(parsed, false);
  }

  baseUrl(url: string, trailingSlash = true): string {
    const parsed = this.parse(url);
    parsed.slugs = [];
    delete parsed.hash;
    delete parsed.query;
    return trailingSlash
      ? this.stringify(parsed, !!parsed.host)
      : this.stringify(parsed, !!parsed.host).replace(/\/$/, '');
  }

  relative(path: string): string {
    const parsed = this.parse(path, true);
    return this.stringify(parsed);
  }

  absolute(path: string): string {
    const parsed = this.parse(path, true);
    return this.stringify(parsed, true);
  }
}

export default UrlManager;
